import {
  Badge,
  Button,
  Card,
  Input,
  Modal,
  Rule,
  Section,
  Table,
  Tabs,
  Td,
  Textarea,
  Th,
  Tr,
  useToast,
} from '@memorizecards/ui';
import type { FormEvent, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Carregando, Erro, TituloPagina, Vazio } from '../components/estado';
import { ApiError, api } from '../lib/api';
import { formatBRL, formatDate, formatDateTime } from '../lib/format';
import {
  cotarTrocaDePlano,
  useCancelarAssinatura,
  useConta,
  useEncerrarSessoes,
  useLedger,
  useSessoes,
  useTrocarPlano,
} from '../lib/queries';
import type {
  AssinaturaConta,
  ChangePlanQuote,
  PlanCode,
  SessionUser,
} from '../lib/types';
import { useAuth } from '../state/auth';
import { paragrafosCancelamento, tituloSituacaoAssinatura } from '@memorizecards/shared/cancelamento';

const SITE = 'https://memorizecards.com.br';
const SENHA_MINIMA = 8;

export function Conta(): ReactNode {
  const { data, isPending, error, refetch } = useConta();

  if (isPending) return <Carregando className="py-24" />;
  if (error) {
    return (
      <Section spacing="md">
        <Erro error={error} aoTentarNovamente={() => void refetch()} />
      </Section>
    );
  }

  return (
    <Section spacing="md">
      <TituloPagina
        eyebrow="Sua conta"
        titulo={data!.user.nome || data!.user.email}
        descricao="Dados pessoais, senha, assinatura e histórico de pagamentos."
      />

      <Tabs
        items={[
          { id: 'assinatura', label: 'Assinatura', content: <Assinatura /> },
          { id: 'dados', label: 'Dados pessoais', content: <DadosPessoais /> },
          { id: 'estudo', label: 'Estudo', content: <PreferenciasEstudo /> },
          { id: 'seguranca', label: 'Segurança', content: <Seguranca /> },
          { id: 'historico', label: 'Histórico', content: <Historico /> },
        ]}
      />
    </Section>
  );
}

// ── Assinatura ──────────────────────────────────────────────────────────────

function Assinatura(): ReactNode {
  const { data, isPending, error, refetch } = useConta();
  const trocar = useTrocarPlano();
  const cancelar = useCancelarAssinatura();
  const { recarregar } = useAuth();
  const toast = useToast();

  const [planoAlvo, setPlanoAlvo] = useState<PlanCode | null>(null);
  const [cotacao, setCotacao] = useState<ChangePlanQuote | null>(null);
  const [cotando, setCotando] = useState(false);
  const [cancelarAberto, setCancelarAberto] = useState(false);
  const [motivo, setMotivo] = useState('');

  if (isPending) return <Carregando />;
  if (error) return <Erro error={error} aoTentarNovamente={() => void refetch()} />;

  const { user, periodo, planos, assinatura } = data!;
  const acesso = user.acesso;
  const planoAtual = periodo?.plano ?? user.plano;

  async function abrirTroca(code: PlanCode) {
    setPlanoAlvo(code);
    setCotacao(null);
    setCotando(true);
    try {
      setCotacao(await cotarTrocaDePlano(code));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Não consegui calcular o valor.');
      setPlanoAlvo(null);
    } finally {
      setCotando(false);
    }
  }

  return (
    <div className="flex flex-col gap-8">
      {assinatura.cancelamento ? (
        <AvisoCancelamento assinatura={assinatura} user={user} />
      ) : null}

      <Card
        padding="lg"
        active={acesso.liberado}
        className="flex flex-wrap items-start justify-between gap-6"
      >
        <div>
          <p className="mc-mono">Situação</p>
          <p className="mt-2 font-mc-display text-2xl font-semibold text-mc-ink">
            {rotuloDaSituacao(user, assinatura)}
          </p>

          {periodo ? (
            <dl className="mt-5 flex flex-col gap-1.5 text-sm">
              <Linha rotulo="Plano" valor={planos[periodo.plano as PlanCode]?.name ?? periodo.plano} />
              <Linha rotulo="Início" valor={formatDate(periodo.inicio)} />
              <Linha rotulo="Vence em" valor={formatDate(periodo.fim)} />
              <Linha rotulo="Valor pago" valor={formatBRL(periodo.pagoCentavos)} />
            </dl>
          ) : (
            <p className="mt-4 max-w-md text-sm text-mc-ink2">
              Você ainda não tem uma assinatura ativa. Escolha um plano abaixo para
              liberar todos os flashcards.
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-3">
          {acesso.diasRestantes !== null ? (
            <div className="text-right">
              <p className="mc-mono">Dias restantes</p>
              <p
                className={`font-mc-display text-4xl font-semibold tabular-nums ${
                  acesso.diasRestantes <= 7 ? 'text-mc-warning' : 'text-mc-accent'
                }`}
              >
                {acesso.diasRestantes}
              </p>
            </div>
          ) : null}

          {user.saldoCentavos > 0 ? (
            <Badge tone="accent">
              Crédito de {formatBRL(user.saldoCentavos)}
            </Badge>
          ) : null}
        </div>
      </Card>

      <div>
        <h2 className="mb-4 text-mc-card-title font-medium text-mc-ink">
          {planoAtual ? 'Trocar de plano' : 'Escolher um plano'}
        </h2>

        <div className="grid gap-4 md:grid-cols-3">
          {(Object.values(planos) as Array<(typeof planos)[PlanCode]>).map((plano) => {
            const atual = plano.code === planoAtual;
            return (
              <Card
                key={plano.code}
                padding="lg"
                active={atual || plano.highlight === true}
                dimmed={!atual && plano.highlight !== true}
                className="flex flex-col gap-4"
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-mc-card-title font-medium text-mc-ink">
                    {plano.name}
                  </h3>
                  {atual ? (
                    <Badge tone="accent">Atual</Badge>
                  ) : plano.savingsLabel ? (
                    <Badge tone="neutral">{plano.savingsLabel}</Badge>
                  ) : null}
                </div>

                <div>
                  <p className="font-mc-display text-3xl font-semibold text-mc-ink">
                    {formatBRL(plano.priceCents)}
                  </p>
                  <p className="mc-mono mt-1 normal-case tracking-normal">
                    {formatBRL(plano.monthlyEquivalentCents)} por mês
                  </p>
                </div>

                <Button
                  variant={atual ? 'ghost' : 'primary'}
                  disabled={atual}
                  fullWidth
                  className="mt-auto"
                  onClick={() => void abrirTroca(plano.code)}
                >
                  {atual ? 'Plano atual' : planoAtual ? 'Simular troca' : 'Assinar'}
                </Button>
              </Card>
            );
          })}
        </div>
      </div>

      {periodo && assinatura.podeCancelar ? (
        <>
          <Rule />
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-mc-ink">Quer cancelar?</p>
              <p className="mt-1 max-w-xl text-sm text-mc-ink2">
                {assinatura.preview?.outcome === 'full_refund' ||
                (assinatura.preview?.refundCents ?? 0) > 0
                  ? `Dentro de 7 dias da compra devolvemos ${formatBRL(assinatura.preview?.refundCents ?? periodo.pagoCentavos)} integralmente e encerramos o acesso.`
                  : `Depois dos 7 dias de arrependimento, cancelamos a renovação e seu acesso continua até ${formatDate(periodo.fim)}.`}
              </p>
            </div>
            <Button variant="danger" onClick={() => setCancelarAberto(true)}>
              Cancelar assinatura
            </Button>
          </div>
        </>
      ) : null}

      <ModalTroca
        planoAlvo={planoAlvo}
        cotacao={cotacao}
        cotando={cotando}
        aplicando={trocar.isPending}
        nomeDoPlano={planoAlvo ? planos[planoAlvo]?.name ?? planoAlvo : ''}
        aoFechar={() => {
          setPlanoAlvo(null);
          setCotacao(null);
        }}
        aoConfirmar={() => {
          if (!planoAlvo) return;
          trocar.mutate(
            { planCode: planoAlvo },
            {
              onSuccess: (res) => {
                if (res.precisaPagar) {
                  // O pagamento vive no site de vendas; a plataforma não
                  // processa cartão nenhum.
                  window.location.href = `${SITE}/checkout?plano=${planoAlvo}&email=${encodeURIComponent(
                    user.email,
                  )}`;
                  return;
                }
                toast.success('Plano alterado.');
                setPlanoAlvo(null);
                setCotacao(null);
                void recarregar();
              },
              onError: (e) =>
                toast.error(e instanceof Error ? e.message : 'Não consegui trocar.'),
            },
          );
        }}
      />

      <Modal
        open={cancelarAberto}
        onClose={() => setCancelarAberto(false)}
        title="Cancelar assinatura"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCancelarAberto(false)}>
              Manter minha assinatura
            </Button>
            <Button
              variant="danger"
              loading={cancelar.isPending}
              onClick={() =>
                cancelar.mutate(
                  motivo.trim() ? { motivo: motivo.trim() } : {},
                  {
                    onSuccess: async () => {
                      setCancelarAberto(false);
                      setMotivo('');
                      await refetch();
                      await recarregar();
                      toast.success('Cancelamento confirmado. Veja os detalhes abaixo.');
                    },
                    onError: (e) =>
                      toast.error(
                        e instanceof Error ? e.message : 'Não consegui cancelar.',
                      ),
                  },
                )
              }
            >
              Confirmar cancelamento
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-5">
          <p className="text-sm text-mc-ink2">
            {periodo && assinatura.preview
              ? assinatura.preview.refundCents > 0
                ? `Compra recente: devolvemos ${formatBRL(assinatura.preview.refundCents)} integralmente e encerramos o acesso. O prazo depende do meio de pagamento (Pix, cartão ou boleto).`
                : `Seu acesso está pago até ${formatDate(periodo.fim)}. Fora dos 7 dias de arrependimento não há reembolso — você estuda até o fim do período.`
              : ''}
          </p>
          <Textarea
            label="Por que está cancelando? (opcional)"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            maxLength={400}
            className="min-h-28"
            hint="Sua resposta nos ajuda a melhorar a plataforma."
          />
        </div>
      </Modal>
    </div>
  );
}

function ModalTroca({
  planoAlvo,
  cotacao,
  cotando,
  aplicando,
  nomeDoPlano,
  aoFechar,
  aoConfirmar,
}: {
  planoAlvo: PlanCode | null;
  cotacao: ChangePlanQuote | null;
  cotando: boolean;
  aplicando: boolean;
  nomeDoPlano: string;
  aoFechar: () => void;
  aoConfirmar: () => void;
}): ReactNode {
  return (
    <Modal
      open={planoAlvo !== null}
      onClose={aoFechar}
      title={`Mudar para ${nomeDoPlano}`}
      description="Veja o cálculo antes de confirmar. Nada é cobrado nesta tela."
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={aoFechar}>
            Voltar
          </Button>
          <Button loading={aplicando} disabled={cotando || !cotacao} onClick={aoConfirmar}>
            {cotacao && cotacao.chargeCents > 0 ? 'Ir para o pagamento' : 'Confirmar troca'}
          </Button>
        </>
      }
    >
      {cotando ? <Carregando /> : null}

      {cotacao ? (
        <div className="flex flex-col gap-5">
          <dl className="flex flex-col gap-2.5 text-sm">
            <Linha
              rotulo="Dias que sobram do plano atual"
              valor={String(cotacao.remainingDays)}
            />
            <Linha
              rotulo="Crédito pelo tempo não usado"
              valor={formatBRL(cotacao.unusedCreditCents)}
              tom="accent"
            />
            {cotacao.balanceAppliedCents > 0 ? (
              <Linha
                rotulo="Crédito na sua conta"
                valor={formatBRL(cotacao.balanceAppliedCents)}
                tom="accent"
              />
            ) : null}
            <Rule className="my-1" />
            <Linha
              rotulo="Valor a pagar agora"
              valor={formatBRL(cotacao.chargeCents)}
              destaque
            />
            {cotacao.newBalanceCents > 0 ? (
              <Linha
                rotulo="Sobra como crédito futuro"
                valor={formatBRL(cotacao.newBalanceCents)}
                tom="accent"
              />
            ) : null}
            <Linha
              rotulo="Nova validade"
              valor={formatDate(cotacao.newExpiresAt)}
            />
          </dl>

          <p className="rounded-mc-field border border-mc-line bg-[rgba(0,0,0,0.2)] px-4 py-3 text-sm text-mc-ink2">
            {cotacao.chargeCents > 0
              ? 'Você será levado ao checkout para concluir o pagamento. A troca só vale depois da aprovação.'
              : 'O crédito cobre a troca inteira. Ela é aplicada na hora, sem pagamento.'}
          </p>
        </div>
      ) : null}
    </Modal>
  );
}

// ── Dados pessoais ──────────────────────────────────────────────────────────

function DadosPessoais(): ReactNode {
  const { user, recarregar, definirUsuario } = useAuth();
  const toast = useToast();

  const [nome, setNome] = useState(user?.nome ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    setNome(user?.nome ?? '');
    setEmail(user?.email ?? '');
  }, [user?.nome, user?.email]);

  const mudou = nome !== (user?.nome ?? '') || email !== (user?.email ?? '');

  async function salvar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      const res = await api<{ user: SessionUser }>('/auth/profile', {
        method: 'PATCH',
        body: { nome: nome.trim() || null, email: email.trim() },
      });
      definirUsuario(res.user);
      toast.success('Dados atualizados.');
      void recarregar();
    } catch (e2) {
      setErro(e2 instanceof ApiError ? e2.message : 'Não consegui salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Card padding="lg" className="max-w-xl">
      <form onSubmit={salvar} className="flex flex-col gap-5" noValidate>
        <Input
          label="Nome"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Como quer ser chamado"
          autoComplete="name"
        />
        <Input
          label="E-mail"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          hint="É por aqui que você entra e recebe os avisos da assinatura."
        />

        {erro ? (
          <p
            className="rounded-mc-field border border-mc-danger/40 bg-mc-danger/10 px-4 py-3 text-sm text-mc-ink"
            role="alert"
          >
            {erro}
          </p>
        ) : null}

        <Button type="submit" loading={salvando} disabled={!mudou} className="self-start">
          Salvar alterações
        </Button>
      </form>
    </Card>
  );
}

function PreferenciasEstudo(): ReactNode {
  const toast = useToast();
  const [novosPorDia, setNovosPorDia] = useState(20);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [original, setOriginal] = useState(20);

  useEffect(() => {
    let vivo = true;
    void api<{ novosPorDia: number }>('/conta/estudo')
      .then((res) => {
        if (!vivo) return;
        setNovosPorDia(res.novosPorDia);
        setOriginal(res.novosPorDia);
      })
      .catch((e) => {
        if (!vivo) return;
        setErro(e instanceof ApiError ? e.message : 'Não consegui carregar.');
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });
    return () => {
      vivo = false;
    };
  }, []);

  async function salvar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      const res = await api<{ novosPorDia: number }>('/conta/estudo', {
        method: 'PATCH',
        body: { novosPorDia },
      });
      setNovosPorDia(res.novosPorDia);
      setOriginal(res.novosPorDia);
      toast.success('Preferências de estudo salvas.');
    } catch (e2) {
      setErro(e2 instanceof ApiError ? e2.message : 'Não consegui salvar.');
    } finally {
      setSalvando(false);
    }
  }

  if (carregando) return <Carregando />;

  return (
    <Card padding="lg" className="max-w-xl">
      <p className="mb-5 text-sm text-mc-ink2">
        Quantos cards novos por dia você quer ver em cada matéria. Revisões vencidas
        sempre entram antes dos novos.
      </p>
      <form onSubmit={salvar} className="flex flex-col gap-5" noValidate>
        <Input
          label="Cards novos por dia"
          type="number"
          min={0}
          max={999}
          value={novosPorDia}
          onChange={(e) => setNovosPorDia(Number(e.target.value))}
          hint="0 desliga cards novos — só revisões e aprendizagem."
        />

        {erro ? (
          <p
            className="rounded-mc-field border border-mc-danger/40 bg-mc-danger/10 px-4 py-3 text-sm text-mc-ink"
            role="alert"
          >
            {erro}
          </p>
        ) : null}

        <Button
          type="submit"
          loading={salvando}
          disabled={novosPorDia === original}
          className="self-start"
        >
          Salvar
        </Button>
      </form>
    </Card>
  );
}

// ── Segurança ───────────────────────────────────────────────────────────────

function Seguranca(): ReactNode {
  const { sair } = useAuth();
  const navigate = useNavigate();
  const { data: sessoes, isPending, error, refetch } = useSessoes();
  const encerrar = useEncerrarSessoes();
  const toast = useToast();

  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erroSenha, setErroSenha] = useState<string | null>(null);
  const [trocando, setTrocando] = useState(false);

  async function trocarSenha(e: FormEvent) {
    e.preventDefault();
    setErroSenha(null);

    if (novaSenha.length < SENHA_MINIMA) {
      setErroSenha(`A senha precisa de pelo menos ${SENHA_MINIMA} caracteres.`);
      return;
    }
    if (novaSenha !== confirmacao) {
      setErroSenha('As duas senhas não são iguais.');
      return;
    }

    setTrocando(true);
    try {
      await api('/auth/change-password', {
        method: 'POST',
        body: { senhaAtual, novaSenha },
      });
      toast.success('Senha alterada. Entre de novo.');
      await sair();
      navigate('/entrar', { replace: true });
    } catch (e2) {
      setErroSenha(e2 instanceof ApiError ? e2.message : 'Não consegui trocar a senha.');
      setTrocando(false);
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card padding="lg">
        <h2 className="text-mc-card-title font-medium text-mc-ink">Trocar senha</h2>
        <p className="mt-1 text-sm text-mc-ink2">
          Ao trocar, todos os aparelhos são desconectados — inclusive este.
        </p>

        <form onSubmit={trocarSenha} className="mt-6 flex flex-col gap-5" noValidate>
          <Input
            label="Senha atual"
            type="password"
            required
            autoComplete="current-password"
            value={senhaAtual}
            onChange={(e) => setSenhaAtual(e.target.value)}
          />
          <Input
            label="Nova senha"
            type="password"
            required
            autoComplete="new-password"
            value={novaSenha}
            onChange={(e) => setNovaSenha(e.target.value)}
            hint={`Mínimo de ${SENHA_MINIMA} caracteres.`}
          />
          <Input
            label="Repita a nova senha"
            type="password"
            required
            autoComplete="new-password"
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
          />

          {erroSenha ? (
            <p
              className="rounded-mc-field border border-mc-danger/40 bg-mc-danger/10 px-4 py-3 text-sm text-mc-ink"
              role="alert"
            >
              {erroSenha}
            </p>
          ) : null}

          <Button type="submit" loading={trocando} className="self-start">
            Trocar senha
          </Button>
        </form>
      </Card>

      <Card padding="lg">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-mc-card-title font-medium text-mc-ink">
              Aparelhos conectados
            </h2>
            <p className="mt-1 text-sm text-mc-ink2">
              Se vir algo que não reconhece, encerre tudo e troque a senha.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            loading={encerrar.isPending}
            onClick={() =>
              encerrar.mutate(undefined, {
                onSuccess: async (r) => {
                  toast.success(`${r.encerradas} sessões encerradas.`);
                  await sair();
                  navigate('/entrar', { replace: true });
                },
                onError: () => toast.error('Não consegui encerrar.'),
              })
            }
          >
            Encerrar todas
          </Button>
        </div>

        {isPending ? <Carregando /> : null}
        {error ? <Erro error={error} aoTentarNovamente={() => void refetch()} /> : null}

        {sessoes && sessoes.length > 0 ? (
          <ul className="mt-5 flex flex-col divide-y divide-mc-line">
            {sessoes.map((s, i) => (
              <li key={i} className="py-3">
                <p className="truncate text-sm text-mc-ink">
                  {resumoDoAgente(s.user_agent)}
                </p>
                <p className="mc-mono mt-0.5 normal-case tracking-normal">
                  {s.ip ?? 'IP desconhecido'} · visto em{' '}
                  {formatDateTime(s.last_seen_at ?? s.created_at)}
                </p>
              </li>
            ))}
          </ul>
        ) : null}
      </Card>
    </div>
  );
}

// ── Histórico ───────────────────────────────────────────────────────────────

const ROTULOS_MOVIMENTO: Record<string, string> = {
  purchase: 'Compra',
  renewal: 'Renovação',
  upgrade: 'Upgrade',
  downgrade: 'Downgrade',
  cancellation: 'Cancelamento',
  refund: 'Reembolso',
  credit: 'Crédito',
  adjustment: 'Ajuste',
};

function Historico(): ReactNode {
  const { data, isPending, error, refetch } = useLedger();

  if (isPending) return <Carregando />;
  if (error) return <Erro error={error} aoTentarNovamente={() => void refetch()} />;

  if (!data || data.length === 0) {
    return (
      <Vazio
        titulo="Nenhum movimento ainda"
        descricao="Compras, renovações, trocas de plano e reembolsos aparecem aqui."
      />
    );
  }

  return (
    <Table>
      <thead>
        <tr>
          <Th>Data</Th>
          <Th>Movimento</Th>
          <Th>Plano</Th>
          <Th className="text-right">Valor</Th>
          <Th className="text-right">Crédito</Th>
          <Th>Nova validade</Th>
        </tr>
      </thead>
      <tbody>
        {data.map((m, i) => (
          <Tr key={i}>
            <Td className="whitespace-nowrap">{formatDate(m.created_at)}</Td>
            <Td>{ROTULOS_MOVIMENTO[m.kind] ?? m.kind}</Td>
            <Td>
              {m.from_plan && m.to_plan && m.from_plan !== m.to_plan
                ? `${m.from_plan} → ${m.to_plan}`
                : (m.to_plan ?? m.from_plan ?? '—')}
            </Td>
            <Td
              className={`text-right tabular-nums ${
                m.amount_cents < 0 ? 'text-mc-accent' : 'text-mc-ink'
              }`}
            >
              {formatBRL(m.amount_cents)}
            </Td>
            <Td className="text-right tabular-nums">
              {m.credit_cents > 0 ? formatBRL(m.credit_cents) : '—'}
            </Td>
            <Td className="whitespace-nowrap">
              {m.expires_after ? formatDate(m.expires_after) : '—'}
            </Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}

// ── Auxiliares ──────────────────────────────────────────────────────────────

function Linha({
  rotulo,
  valor,
  tom,
  destaque = false,
}: {
  rotulo: string;
  valor: string;
  tom?: 'accent';
  destaque?: boolean;
}): ReactNode {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className={destaque ? 'text-mc-ink' : 'text-mc-ink3'}>{rotulo}</dt>
      <dd
        className={`tabular-nums ${
          destaque
            ? 'font-mc-display text-xl font-semibold text-mc-ink'
            : tom === 'accent'
              ? 'text-mc-accent'
              : 'text-mc-ink2'
        }`}
      >
        {valor}
      </dd>
    </div>
  );
}

function AvisoCancelamento({
  assinatura,
  user,
}: {
  assinatura: AssinaturaConta;
  user: SessionUser;
}): ReactNode {
  const info = assinatura.cancelamento;
  if (!info) return null;

  const linhas = paragrafosCancelamento({
    outcome: info.outcome,
    refundCents: info.refundCents,
    accessUntil: info.accessUntil,
    metodoPagamento: info.metodoPagamento,
    reembolsoStatus: info.reembolsoStatus,
    revokeImmediately: info.revokeImmediately,
    formatarMoeda: formatBRL,
    formatarData: formatDate,
  });

  return (
    <Card
      padding="lg"
      className="border-mc-warning/40 bg-mc-warning/5"
      role="status"
      aria-live="polite"
    >
      <p className="mc-mono text-mc-warning">Cancelamento</p>
      <h2 className="mt-2 text-mc-card-title font-medium text-mc-ink">
        {tituloSituacaoAssinatura(assinatura.canceladoEm, user.status)}
      </h2>
      <p className="mt-1 text-sm text-mc-ink3">
        Confirmado em {formatDateTime(info.canceladoEm)}
      </p>
      <ul className="mt-4 flex list-disc flex-col gap-2 pl-5 text-sm text-mc-ink2">
        {linhas.map((linha) => (
          <li key={linha}>{linha}</li>
        ))}
      </ul>
    </Card>
  );
}

function rotuloDaSituacao(user: SessionUser, assinatura: AssinaturaConta): string {
  if (user.role !== 'user') return 'Acesso administrativo';
  if (assinatura.canceladoEm) {
    return tituloSituacaoAssinatura(assinatura.canceladoEm, user.status);
  }
  switch (user.acesso.motivo) {
    case 'ok':
      return 'Assinatura ativa';
    case 'cancelled':
      return 'Assinatura cancelada';
    case 'expired':
      return 'Assinatura vencida';
    case 'blocked':
      return 'Conta bloqueada';
    case 'no_plan':
      return 'Sem assinatura';
    default:
      return 'Ativa';
  }
}

/** "Chrome no Windows" a partir do user agent, sem depender de biblioteca. */
function resumoDoAgente(agente: string | null): string {
  if (!agente) return 'Aparelho desconhecido';

  const navegador =
    /Edg\//.test(agente)
      ? 'Edge'
      : /OPR\//.test(agente)
        ? 'Opera'
        : /Chrome\//.test(agente)
          ? 'Chrome'
          : /Safari\//.test(agente)
            ? 'Safari'
            : /Firefox\//.test(agente)
              ? 'Firefox'
              : 'Navegador';

  const sistema = /Android/.test(agente)
    ? 'Android'
    : /iPhone|iPad|iOS/.test(agente)
      ? 'iOS'
      : /Windows/.test(agente)
        ? 'Windows'
        : /Mac OS X/.test(agente)
          ? 'macOS'
          : /Linux/.test(agente)
            ? 'Linux'
            : 'sistema desconhecido';

  return `${navegador} no ${sistema}`;
}
